import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Skeleton } from '@mui/material';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import { styled } from '@mui/material/styles';
import Button from '@components/Button';
import { Input } from '@components/Input';
import { ModalBase } from '@components/Modal';
import { Typography } from '@components/Typography';
import { alertActions } from '@actions';
import { ExternalRoutes } from '@constants/routes';
import { handleApiError } from '@helpers/api';
import { formatDateOrEmpty } from '@helpers/dates';
import { useTranslation } from '@hooks';
import { profilesService } from '@services';

const Modal = styled(ModalBase)({
    '& .modal-base-paper': {
        outline: 'none',
        maxWidth: 480,
        width: 'calc(100% - 32px)',
    },
    '& .modal-base-content': {
        padding: 16,
    },
});

const STEP = {
    METHOD_SELECTION: 'METHOD_SELECTION',
    PIN_ENTRY: 'PIN_ENTRY',
};

const NOTICE = {
    CHECKING: 'CHECKING',
    STILL_OPEN: 'STILL_OPEN',
    FAILED: 'FAILED',
    LOOKUP_FAILED: 'LOOKUP_FAILED',
};

const VERIFY_ON_GOOGLE = 'VERIFY_ON_GOOGLE';

const METHODS = {
    PHONE_CALL: { collectsCode: true },
    SMS: { collectsCode: true },
    EMAIL: { collectsCode: true },
    ADDRESS: { collectsCode: true, mailed: true },
    AUTO: {},
    VETTED_PARTNER: {},
};

const collectsCode = method => Boolean(METHODS[method]?.collectsCode);
const isMailed = method => Boolean(METHODS[method]?.mailed);

const VERIFY_ON_GOOGLE_OPTION = { verificationMethod: VERIFY_ON_GOOGLE };

const isRequestableOption = option =>
    Boolean(option?.verificationMethod) && option.verificationMethod !== 'VERIFICATION_METHOD_UNSPECIFIED';

const readVerification = result => ({
    name: result?.name || '',
    method: result?.method || '',
    state: result?.state || '',
    createTime: result?.createTime || null,
    announcement: result?.announcement || '',
});

const isResumable = verification =>
    Boolean(verification.name) && verification.state === 'PENDING' && collectsCode(verification.method);

const findSoleOptionIndex = (options, method) => {
    let sole = -1;
    for (let index = 0; index < options.length; index += 1) {
        if (options[index].verificationMethod !== method) continue;
        if (sole >= 0) return -1;
        sole = index;
    }
    return sole;
};

const formatPostalAddress = address =>
    [
        ...(address?.addressLines || []),
        address?.sublocality,
        address?.locality,
        address?.administrativeArea,
        address?.postalCode,
    ]
        .filter(Boolean)
        .join(', ');

const VerificationModal = ({ open, onClose, onVerified, profileId, googleAccountId }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const language = useSelector(state => state.auth.user?.language);
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [step, setStep] = useState(STEP.METHOD_SELECTION);
    const [activeVerification, setActiveVerification] = useState(null);
    const [notice, setNotice] = useState(null);
    const [emailUser, setEmailUser] = useState('');
    const [pin, setPin] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [resending, setResending] = useState(false);

    const allOptions = useMemo(() => [...options, VERIFY_ON_GOOGLE_OPTION], [options]);
    const selectedOption = allOptions[selectedIndex];

    useEffect(() => {
        if (!open || !profileId) return undefined;

        let cancelled = false;
        setLoading(true);
        setStep(STEP.METHOD_SELECTION);
        setActiveVerification(null);
        setNotice(null);
        setPin('');
        setSelectedIndex(0);
        setOptions([]);

        let lookupFailed = false;
        Promise.all([
            profilesService.getVerificationOptions(profileId),
            profilesService.getCurrentVerification(profileId).catch(() => {
                lookupFailed = true;
                return null;
            }),
        ])
            .then(([optionsData, latestData]) => {
                if (cancelled) return;

                const usableOptions = (Array.isArray(optionsData) ? optionsData : []).filter(isRequestableOption);
                setOptions(usableOptions);

                const latest = readVerification(latestData);
                if (isResumable(latest)) {
                    const soleIndex = findSoleOptionIndex(usableOptions, latest.method);
                    if (soleIndex >= 0) {
                        setSelectedIndex(soleIndex);
                    }
                    setActiveVerification({ ...latest, resumed: true, boundToOption: soleIndex >= 0, contact: '' });
                    setStep(STEP.PIN_ENTRY);
                } else if (lookupFailed) {
                    setNotice({ kind: NOTICE.LOOKUP_FAILED });
                } else if (latest.state === 'PENDING') {
                    setNotice({ kind: NOTICE.CHECKING });
                } else if (latest.state === 'FAILED') {
                    setNotice({ kind: NOTICE.FAILED, method: latest.method, createTime: latest.createTime });
                }
            })
            .catch(error => {
                if (!cancelled) handleApiError(error);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, profileId]);

    useEffect(() => {
        setEmailUser(selectedOption?.emailData?.user || '');
    }, [selectedOption]);

    const isEditingMailbox = Boolean(
        selectedOption?.verificationMethod === 'EMAIL' && selectedOption.emailData?.isUserNameEditable,
    );

    const requestedEmailAddress = (() => {
        const emailData = selectedOption?.emailData;
        if (!emailData) return '';
        const user = isEditingMailbox ? emailUser : emailData.user;
        return user ? `${user}@${emailData.domain}` : '';
    })();

    const currentMethod = activeVerification?.method || '';
    const deliveryDays =
        activeVerification && !activeVerification.resumed
            ? selectedOption?.addressData?.expectedDeliveryDaysRegion
            : null;
    const canResend = Boolean(activeVerification?.boundToOption);
    const mailboxMissing = isEditingMailbox && !emailUser;

    const METHOD_LABELS = {
        PHONE_CALL: t('GoogleVerification.PHONE_CALL'),
        SMS: t('GoogleVerification.SMS'),
        EMAIL: t('GoogleVerification.EMAIL'),
        ADDRESS: t('GoogleVerification.ADDRESS'),
        AUTO: t('GoogleVerification.AUTO'),
        VETTED_PARTNER: t('GoogleVerification.VETTED_PARTNER'),
        [VERIFY_ON_GOOGLE]: t('GoogleVerification.verifyOnGoogle'),
    };

    const methodLabel = method => METHOD_LABELS[method] || method;

    const optionContact = option => {
        if (!option) return '';
        if (option.verificationMethod === 'EMAIL') {
            return option === selectedOption
                ? requestedEmailAddress
                : (option.emailData && `${option.emailData.user}@${option.emailData.domain}`) || '';
        }
        return option.phoneNumber || '';
    };

    const doRequestVerification = async () =>
        profilesService.requestVerification(
            profileId,
            selectedOption.verificationMethod,
            selectedOption.phoneNumber,
            requestedEmailAddress || undefined,
        );

    const startVerification = result => {
        const verification = readVerification(result);
        if (!verification.name) {
            handleApiError(null);
            return false;
        }
        setActiveVerification({
            ...verification,
            resumed: false,
            boundToOption: true,
            contact: optionContact(selectedOption),
        });
        setPin('');
        return true;
    };

    const finishVerified = () => {
        dispatch(alertActions.success(t('GoogleVerification.verificationComplete')));
        onVerified?.();
        onClose();
    };

    const finishWithoutCode = verification => {
        if (verification.state === 'COMPLETED') {
            finishVerified();
            return;
        }
        if (verification.state === 'FAILED') {
            dispatch(alertActions.error(t('GoogleVerification.verificationFailed')));
            onClose();
            return;
        }
        dispatch(alertActions.success(verification.announcement || t('GoogleVerification.checkInProgress')));
        onClose();
    };

    const onNext = async () => {
        if (!selectedOption) return;
        if (selectedOption.verificationMethod === VERIFY_ON_GOOGLE) {
            const link = googleAccountId
                ? ExternalRoutes.GoogleBusinessGroups(googleAccountId)
                : ExternalRoutes.GoogleBusinessLocations;
            window.open(link, '_blank');
            onClose();
            return;
        }
        setSubmitting(true);
        try {
            const result = await doRequestVerification();
            const verification = readVerification(result);
            if (collectsCode(verification.method) && verification.state === 'PENDING') {
                if (startVerification(result)) {
                    setStep(STEP.PIN_ENTRY);
                }
            } else {
                finishWithoutCode(verification);
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const onBack = () => {
        if (activeVerification) {
            setNotice({
                kind: NOTICE.STILL_OPEN,
                method: activeVerification.method,
                createTime: activeVerification.createTime,
            });
        }
        setStep(STEP.METHOD_SELECTION);
        setPin('');
        setActiveVerification(null);
    };

    const onResend = async () => {
        if (resending) return;
        setResending(true);
        try {
            const result = await doRequestVerification();
            if (!startVerification(result)) {
                onBack();
            }
        } catch (error) {
            handleApiError(error);
        } finally {
            setResending(false);
        }
    };

    const onSubmitPin = async () => {
        if (!pin || !activeVerification) return;
        setSubmitting(true);
        const verificationId = activeVerification.name.split('/').pop();
        try {
            await profilesService.completeVerification(profileId, verificationId, pin);
            finishVerified();
        } catch (error) {
            handleApiError(error);
        } finally {
            setSubmitting(false);
        }
    };

    const getNoticeText = () => {
        if (!notice) return '';
        const noticeDate = formatDateOrEmpty(notice.createTime, 'dd MMM yyyy', language);

        if (notice.kind === NOTICE.LOOKUP_FAILED) return t('GoogleVerification.latestLookupFailed');
        if (notice.kind === NOTICE.CHECKING) return t('GoogleVerification.checkInProgress');
        if (notice.kind === NOTICE.STILL_OPEN) {
            return noticeDate
                ? t('GoogleVerification.pendingStillOpenOn', { method: methodLabel(notice.method), date: noticeDate })
                : t('GoogleVerification.pendingStillOpen', { method: methodLabel(notice.method) });
        }
        return noticeDate
            ? t('GoogleVerification.lastAttemptFailedOn', { method: methodLabel(notice.method), date: noticeDate })
            : t('GoogleVerification.lastAttemptFailed', { method: methodLabel(notice.method) });
    };

    const getCodeSentDescription = () => {
        if (!currentMethod) return '';
        const sentOn = formatDateOrEmpty(activeVerification?.createTime, 'dd MMM yyyy', language);

        if (isMailed(currentMethod)) {
            return (
                <>
                    {sentOn
                        ? t('GoogleVerification.postcardMailedOn', { date: sentOn })
                        : t('GoogleVerification.postcardMailed')}{' '}
                    {deliveryDays
                        ? t('GoogleVerification.postcardArrives', { days: deliveryDays })
                        : t('GoogleVerification.postcardComeBack')}
                </>
            );
        }

        const contact = activeVerification?.contact || '';
        if (contact) {
            return (
                <>
                    {t('GoogleVerification.pinSentTo')} <strong>{contact}</strong>
                </>
            );
        }
        return sentOn ? t('GoogleVerification.pinSentOn', { date: sentOn }) : t('GoogleVerification.pinSent');
    };

    const getMethodLabel = option => {
        const base = methodLabel(option.verificationMethod);
        const contact = option === selectedOption && isEditingMailbox ? '' : optionContact(option);
        return contact ? `${base} (${contact})` : base;
    };

    const renderSelectedOptionDetails = () => {
        const { verificationMethod, addressData, emailData, announcement } = selectedOption || {};

        if (verificationMethod === 'ADDRESS' && addressData) {
            const postalAddress = formatPostalAddress(addressData.address);
            return (
                <Typography variant="body2" color="textSecondary" sx={{ marginTop: '12px' }}>
                    {t('GoogleVerification.postcardTo')} <strong>{addressData.business}</strong>
                    {postalAddress ? `, ${postalAddress}` : ''}
                    {addressData.expectedDeliveryDaysRegion
                        ? ` ${t('GoogleVerification.postcardArrives', {
                              days: addressData.expectedDeliveryDaysRegion,
                          })}`
                        : ''}
                </Typography>
            );
        }

        if (isEditingMailbox) {
            return (
                <div style={{ marginTop: '12px' }}>
                    <Input
                        label={t('GoogleVerification.mailboxName')}
                        inputProps={{
                            value: emailUser,
                            onChange: e => setEmailUser(e.target.value.replace(/\s/g, '')),
                        }}
                        helperText={requestedEmailAddress || `@${emailData.domain}`}
                        error={mailboxMissing}
                    />
                </div>
            );
        }

        if (verificationMethod === 'VETTED_PARTNER' && announcement) {
            return (
                <Typography variant="body2" color="textSecondary" sx={{ marginTop: '12px' }}>
                    {announcement}
                </Typography>
            );
        }

        return null;
    };

    const isMethodSelection = step === STEP.METHOD_SELECTION;

    return (
        <Modal
            fullScreen
            isOpen={open}
            handleClose={onClose}
            title={t('GoogleVerification.title')}
            onPrimaryAction={isMethodSelection ? onNext : onSubmitPin}
            primaryActionText={isMethodSelection ? t('buttons.continue') : t('common.verify')}
            primaryActionDisabled={submitting || resending || loading || (isMethodSelection ? mailboxMissing : !pin)}
            onSecondaryAction={isMethodSelection ? null : onBack}
            secondaryActionText={t('GoogleVerification.useAnotherMethod')}
            secondaryActionDisabled={submitting || resending}
        >
            {loading && (
                <div style={{ display: 'grid', rowGap: '16px' }}>
                    <Skeleton variant="rounded" width="100%" height={40} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                    <Skeleton variant="rounded" width="100%" height={40} />
                </div>
            )}
            {!loading && isMethodSelection && (
                <>
                    {notice && (
                        <Typography variant="body2" color="textSecondary" sx={{ marginBottom: '12px' }}>
                            {getNoticeText()}
                        </Typography>
                    )}
                    <Typography variant="body2" color="textSecondary" sx={{ marginBottom: '12px' }}>
                        {t('GoogleVerification.selectMethod')}
                    </Typography>
                    <RadioGroup value={String(selectedIndex)} onChange={e => setSelectedIndex(Number(e.target.value))}>
                        {allOptions.map((option, index) => (
                            <FormControlLabel
                                key={index}
                                value={String(index)}
                                control={
                                    <Radio
                                        sx={{
                                            color: 'var(--text-secondary)',
                                            padding: '6px',
                                            '&.Mui-checked': { color: 'var(--primary-main)' },
                                        }}
                                    />
                                }
                                label={
                                    <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                                        {getMethodLabel(option)}
                                    </Typography>
                                }
                                sx={{ marginLeft: '-3px' }}
                            />
                        ))}
                    </RadioGroup>
                    {renderSelectedOptionDetails()}
                </>
            )}
            {!loading && !isMethodSelection && (
                <>
                    <Typography variant="body2" color="textSecondary" sx={{ marginBottom: '16px' }}>
                        {getCodeSentDescription()}
                    </Typography>
                    <Input
                        label={t('GoogleVerification.pinPlaceholder')}
                        inputProps={{
                            value: pin,
                            onChange: e => setPin(e.target.value.replace(/\D/g, '')),
                            maxLength: 10,
                            inputMode: 'numeric',
                            pattern: '[0-9]*',
                        }}
                        disabled={submitting || resending}
                    />
                    {canResend && (
                        <Button
                            variant="outlined"
                            onClick={onResend}
                            disabled={submitting || resending}
                            sx={{ marginTop: '4px' }}
                        >
                            {isMailed(currentMethod) ? t('GoogleVerification.resendPostcard') : t('buttons.resend')}
                        </Button>
                    )}
                </>
            )}
        </Modal>
    );
};

export default VerificationModal;
